import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import StructuredData from '@/components/base/StructuredData';
import { getComicBySlug } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicBySlug(slug || '');

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl font-bold text-foreground-50 mb-4">漫画未找到</h1>
            <Link to="/" className="text-primary-400 hover:text-primary-300">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem' as const, position: 1, name: '首页', item: 'http://lhrkj3.com/' },
      { '@type': 'ListItem' as const, position: 2, name: comic.category, item: `http://lhrkj3.com/category/${comic.categorySlug}` },
      { '@type': 'ListItem' as const, position: 3, name: comic.title, item: `http://lhrkj3.com/comic/${comic.slug}` },
    ],
  };

  const totalChapters = comic.chapters.length;
  const latestChapter = comic.chapters[comic.chapters.length - 1];

  return (
    <>
      <StructuredData type="Organization" />
      <StructuredData type="WebSite" />
      <StructuredData
        type="WebPage"
        data={{
          name: `${comic.title} - 韩漫屋韩漫在线阅读`,
          description: comic.description,
        }}
      />
      <StructuredData type="BreadcrumbList" data={breadcrumbList} />
      <StructuredData
        type="Article"
        data={{
          headline: comic.title,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: comic.updatedAt,
          image: comic.coverImage,
          articleBody: comic.longDescription,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full min-h-[55vh] flex items-center overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(135deg, oklch(0.06 0.002 275) 0%, oklch(0.10 0.015 25) 60%, oklch(0.14 0.04 40) 100%)',
              }}
            ></div>

            <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-24 pb-10">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-400 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-foreground-600"></i>
                <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-400 transition-colors">{comic.category}</Link>
                <i className="ri-arrow-right-s-line text-foreground-600"></i>
                <span className="text-primary-400">{comic.title}</span>
              </nav>

              <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-12">
                <div className="lg:w-[60%] space-y-5">
                  <div className="flex flex-wrap items-center gap-2">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="text-[10px] font-medium bg-accent-500/10 text-accent-400 px-2.5 py-1 rounded-full border border-accent-500/15">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-50 leading-tight">
                    {comic.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <span className="text-foreground-400">
                      <i className="ri-user-line mr-1 text-foreground-500"></i>
                      {comic.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      <span className="text-foreground-200 font-semibold">{comic.rating}</span>
                    </span>
                    <span className="text-foreground-500">
                      <i className="ri-eye-line mr-1"></i>
                      {comic.totalViews.toLocaleString()} 人气
                    </span>
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                      comic.status === '连载中' ? 'bg-primary-500/15 text-primary-400 border border-primary-500/20' : 'bg-accent-500/15 text-accent-400 border border-accent-500/20'
                    }`}>
                      {comic.status}
                    </span>
                  </div>

                  <p className="text-sm md:text-base text-foreground-300 max-w-lg leading-relaxed">
                    {comic.longDescription}
                  </p>

                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={() => document.getElementById('chapter-list')?.scrollIntoView({ behavior: 'smooth' })}
                      className="px-7 py-3 text-sm font-semibold bg-primary-500 text-background-950 rounded-full whitespace-nowrap transition-all duration-200 hover:bg-primary-400 cursor-pointer"
                    >
                      <i className="ri-play-line mr-1.5"></i>
                      开始阅读
                    </button>
                    <button className="px-5 py-3 text-sm font-medium bg-background-100 text-foreground-200 border border-background-200/30 rounded-full whitespace-nowrap transition-all duration-200 hover:border-primary-500/30 hover:text-primary-400 cursor-pointer">
                      <i className="ri-bookmark-line mr-1.5"></i>
                      加入书架
                    </button>
                  </div>
                </div>

                <div className="lg:w-[40%] flex items-start justify-center lg:justify-end mt-2 lg:mt-0">
                  <div className="relative">
                    <div className="absolute -inset-2 bg-primary-500/10 rounded-3xl blur-2xl"></div>
                    <div className="relative w-56 h-72 md:w-64 md:h-80 rounded-2xl overflow-hidden border border-background-200/30">
                      <img
                        src={comic.coverImage}
                        alt={`${comic.title} - 韩漫在线阅读`}
                        title={`${comic.title} - 韩漫屋`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="relative w-full h-48 md:h-60 overflow-hidden">
            <img
              src={comic.bannerImage}
              alt={`${comic.title} 横幅`}
              title={`${comic.title}`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50 via-transparent to-background-50"></div>
          </section>

          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 id="chapter-list" className="text-xl font-bold text-foreground-100">
                  章节列表 <span className="text-foreground-500 text-sm font-normal">(共{totalChapters}话)</span>
                </h2>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1.5 text-xs font-medium bg-background-100 text-foreground-400 rounded-full border border-background-200/30 whitespace-nowrap transition-all hover:border-primary-500/30 hover:text-foreground-200 cursor-pointer">
                    正序
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {comic.chapters.map((chapter, index) => (
                  <div
                    key={chapter.id}
                    data-chapter={chapter.number}
                    className={`group relative rounded-xl overflow-hidden border transition-all duration-200 hover:border-primary-500/20 cursor-pointer ${
                      chapter.isVip
                        ? 'bg-background-100 border-background-200/20'
                        : 'bg-background-100 border-background-200/30'
                    } ${index === 0 ? 'lg:col-span-1 lg:row-span-2' : ''}`}
                  >
                    <div className="flex items-center gap-4 p-4">
                      <div className="w-9 h-9 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-primary-400">{chapter.number}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-200 truncate">{chapter.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[11px] text-foreground-500">{chapter.updatedAt}</span>
                          {chapter.isVip && (
                            <span className="text-[10px] font-semibold text-accent-500 bg-accent-500/10 px-1.5 py-0.5 rounded-full">
                              VIP
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-7 h-7 flex items-center justify-center text-foreground-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        <i className="ri-arrow-right-s-line text-lg"></i>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {latestChapter && (
                <div className="mt-8 text-center">
                  <p className="text-xs text-foreground-500 mb-3">
                    最新更新：{latestChapter.title} · {latestChapter.updatedAt}
                  </p>
                  <button
                    onClick={() => {
                      const firstChapter = document.querySelector('[data-chapter]');
                      firstChapter?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                    className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold bg-primary-500 text-background-950 rounded-full whitespace-nowrap transition-all duration-200 hover:bg-primary-400 cursor-pointer"
                  >
                    <i className="ri-skip-forward-line"></i>
                    从第一话开始阅读
                  </button>
                </div>
              )}
            </div>
          </section>

          {comic.reviews.length > 0 && (
            <section className="py-12 md:py-16 bg-background-100">
              <div className="max-w-[1280px] mx-auto px-4 md:px-6">
                <div className="text-center mb-10">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                    <span className="text-xs font-medium text-primary-400 uppercase tracking-wider">精彩评论</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-50">
                    读者评论
                  </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {comic.reviews.map((review) => (
                    <div
                      key={review.id}
                      className="bg-background-50 rounded-2xl p-5 border border-background-200/30"
                    >
                      <div className="flex items-center gap-0.5 mb-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-600'}`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-foreground-200 font-semibold text-sm mb-2">{review.title}</p>
                      <p className="text-foreground-400 text-xs leading-relaxed mb-4">{review.content}</p>
                      <div className="flex items-center gap-3 pt-3 border-t border-background-200/30">
                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-background-200">
                          <img src={review.userAvatar} alt={review.userName} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="text-foreground-200 text-xs font-medium">{review.userName}</p>
                          <p className="text-foreground-600 text-[11px]">{review.date}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}